import { useTranslation } from 'react-i18next';
import { Table, type TableColumn } from '@/components/Table';
import { OpportunityStatusBadge } from '@/pages/Opportunities/OpportunityStatusBadge';
import { deadlineLabel, moneyLabel } from '@/pages/Opportunities/dates';
import type { Opportunity } from '@/pages/Opportunities/types';
import type { ListPaginationProps } from '@/components/ListPagination';

export interface OpportunityListProps {
    items: Opportunity[]; loading: boolean; error: string | null; onRetry: () => void; onOpen: (item: Opportunity) => void;
    pagination: ListPaginationProps; sort: { key: string; order: 'asc' | 'desc' }; onSort: (sort: { key: string; order: 'asc' | 'desc' }) => void;
}
export const OpportunityList = ({ items, loading, error, onRetry, onOpen, pagination, sort, onSort }: OpportunityListProps) => {
    const { t, i18n } = useTranslation('global');
    const columns: TableColumn<Opportunity>[] = [
        { key: 'code', label: t('opportunities.fields.code'), sortable: true },
        { key: 'workflow_status', label: t('opportunities.state'), sortable: true, render: row => <OpportunityStatusBadge status={row.workflow_status} /> },
        { key: 'submission_at', label: t('opportunities.deadline'), sortable: true, render: row => deadlineLabel(row.submission_at, i18n.language) },
        { key: 'client', label: t('opportunities.fields.client_id'), render: row => row.client?.name ?? '—' },
        { key: 'title', label: t('opportunities.fields.title'), sortable: true },
        { key: 'city', label: t('opportunities.fields.city'), sortable: true, render: row => [row.city, row.province?.name].filter(Boolean).join(', ') },
        { key: 'budget', label: t('opportunities.fields.budget'), sortable: true, render: row => moneyLabel(row.budget, row.currency_code, i18n.language) },
        { key: 'valuation_criteria', label: t('opportunities.fields.valuation_criteria') },
        { key: 'deadline_period', label: t('opportunities.fields.deadline_period') },
    ];
    return <Table appearance="workspace" data={items} columns={columns} pagination={pagination} sort={sort} onSortChange={onSort} manualSorting
        searchable={false} loading={loading} error={error} onRetry={onRetry} onRowClick={onOpen} emptyText={t('opportunities.empty')}
        actions={[{ label: t('opportunities.open'), icon: 'eye', onClick: onOpen }]} />;
};
